import React from "react";
import { Progress } from "../ui/progress";
import { Shield, Crown, Zap } from "lucide-react";
import { KlartextPlan } from "@/lib/klartext-plans";

interface UsageData {
  plan: string;
  limit: number | 'unlimited';
  used: number;
  remaining: number | 'unlimited';
  canCheck: boolean;
}

interface UsageLimitDisplayProps {
  plan?: KlartextPlan;
  usageData?: UsageData;
  onUpgradeClick?: () => void;
}

export function UsageLimitDisplay({ plan = 'free', usageData, onUpgradeClick }: UsageLimitDisplayProps) {
  const isPaidPlan = plan !== 'free';
  
  if (isPaidPlan) {
    const planLabels: Record<KlartextPlan, { label: string; icon: React.ReactNode; color: string }> = {
      free: { label: 'Kostenlos', icon: <Shield className="h-4 w-4" />, color: 'text-slate-600' },
      pro: { label: 'Pro', icon: <Crown className="h-4 w-4" />, color: 'text-yellow-600' },
      familie: { label: 'Familie', icon: <Crown className="h-4 w-4" />, color: 'text-blue-600' },
      firma: { label: 'Firma', icon: <Zap className="h-4 w-4" />, color: 'text-purple-600' },
    };
    
    const planInfo = planLabels[plan];
    
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl">
        <span className={planInfo.color}>{planInfo.icon}</span>
        <span className="font-semibold text-slate-800">{planInfo.label}</span>
        <span className="text-sm text-slate-600">• Unbegrenzte Checks</span>
      </div>
    );
  }
  
  // Use server-side usage data if available
  const limit = usageData?.limit === 'unlimited' ? 3 : (usageData?.limit ?? 3);
  const used = usageData?.used ?? 0;
  const remaining = usageData?.remaining === 'unlimited' ? limit : (usageData?.remaining ?? limit - used);
  const progress = (used / limit) * 100;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 text-slate-600">
          <Shield className="h-4 w-4" />
          Kostenlose Checks
        </span>
        <span className={`font-bold ${remaining === 0 ? 'text-red-600' : remaining <= 1 ? 'text-yellow-600' : 'text-green-600'}`}>
          {remaining}/{limit} übrig
        </span>
      </div>
      
      <Progress 
        value={progress} 
        className="h-2"
      />
      
      {remaining === 0 && (
        <button 
          onClick={onUpgradeClick}
          className="w-full mt-3 px-4 py-2 bg-gradient-to-r from-primary to-blue-600 text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
        >
          <Crown className="h-4 w-4" />
          Unbegrenzt freischalten
        </button>
      )}
      
      {remaining > 0 && remaining <= 1 && (
        <p className="text-xs text-yellow-600 text-center">
          Noch {remaining} kostenloser Check – 
          <button onClick={onUpgradeClick} className="underline font-semibold ml-1">
            Pro werden
          </button>
        </p>
      )}
    </div>
  );
}
